import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './api-client'
import { useApiBase } from './api-base-provider'
import { invalidateLegacyViewerConfig } from './legacy-viewer-cache'

/**
 * All query keys include baseUrl so switching between remotes
 * automatically gives fresh cache entries (no stale cross-remote data).
 */
function useQueryKeyPrefix() {
  const { baseUrl } = useApiBase()
  return ['v2', baseUrl] as const
}

// ─── Projects ────────────────────────────────────────────────

export function useProjects() {
  const api = useApiClient()
  const prefix = useQueryKeyPrefix()
  return useQuery({
    queryKey: [...prefix, 'projects'],
    queryFn: async () => {
      const { data } = await api.get('/api/projects')
      return data as any[]
    },
  })
}

/**
 * This can be done better by enriching the endpoints.
 * Enriched projects list: fetches all projects, then loads each project
 * individually to get viewers (with creator info). Counts apps and
 * distinct creators per project.
 */
export function useProjectsWithCounts(opts?: { isAdmin?: boolean }) {
  const api = useApiClient()
  const prefix = useQueryKeyPrefix()
  return useQuery({
    queryKey: [...prefix, 'projects-with-counts', { isAdmin: !!opts?.isAdmin }],
    staleTime: 60_000,
    retry: 2,
    retryDelay: 3000,
    queryFn: async () => {
      // 1. Get all projects — this is the auth gatekeeper.
      //    If this 401s, React Query surfaces the error and the
      //    interceptor shows the login dialog. We do NOT catch this.
      const { data: projects } = await api.get('/api/projects')

      // 2. Get total user count (admin/superadmin only).
      //    If this fails (403 for non-admins), just use 0.
      let totalUsers = 0
      if (opts?.isAdmin) {
        try {
          const { data: users } = await api.get('/api/users')
          totalUsers = (users ?? []).length
        } catch {
          // Expected for non-admin roles — swallow silently
        }
      }

      // 3. Fetch each project detail to get viewers with creator info.
      //    Auth is already confirmed from step 1, so these shouldn't 401.
      const detailed = await Promise.all(
        (projects as any[]).map(async (p: any) => {
          try {
            const { data } = await api.get(`/api/projects/${p.path}`)
            const viewers = (data.viewers ?? []) as any[]
            const activeViewers = viewers.filter((v: any) => !v.deleted?.at)
            const creatorIds = new Set(
              activeViewers
                .map((v: any) => v.user?.id ?? v.user?.name)
                .filter(Boolean)
            )
            return {
              ...p,
              viewerCount: activeViewers.length,
              userCount: creatorIds.size,
            }
          } catch {
            return { ...p, viewerCount: 0, userCount: 0 }
          }
        })
      )

      return detailed.map((p: any) => ({ ...p, _totalUsers: totalUsers }))
    },
  })
}

export function useProject(projectPath: string) {
  const api = useApiClient()
  const prefix = useQueryKeyPrefix()
  return useQuery({
    queryKey: [...prefix, 'project', projectPath],
    queryFn: async () => {
      const { data } = await api.get(`/api/projects/${projectPath}`)
      return data as any
    },
    enabled: Boolean(projectPath),
    // Apps are also edited in the in-app editor, outside this cache, and
    // queryClient.ts sets refetchOnMount: false app-wide.
    refetchOnMount: 'always',
  })
}

export function useCreateProject() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async (body: { title: string; path?: string }) => {
      const { data } = await api.post('/api/projects', body)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...prefix, 'projects'] })
      qc.invalidateQueries({ queryKey: [...prefix, 'projects-with-counts'] })
    },
  })
}

export function useRenameProject() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({ id, title, path }: { id: string; title?: string; path?: string }) => {
      const { data } = await api.patch(`/api/projects/${id}`, { title, path })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...prefix, 'projects'] })
      qc.invalidateQueries({ queryKey: [...prefix, 'projects-with-counts'] })
      qc.invalidateQueries({ queryKey: [...prefix, 'project'] })
    },
  })
}

// ─── Users ───────────────────────────────────────────────────

export function useUsers() {
  const api = useApiClient()
  const prefix = useQueryKeyPrefix()
  return useQuery({
    queryKey: [...prefix, 'users'],
    queryFn: async () => {
      const { data } = await api.get('/api/users')
      return data as any[]
    },
  })
}

export function useCreateUser() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async (body: Record<string, any>) => {
      const { data } = await api.post('/api/users', body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...prefix, 'users'] }),
  })
}

export function useUpdateUser() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, any>) => {
      const { data } = await api.patch(`/api/users/${id}`, body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...prefix, 'users'] }),
  })
}

export function useDeleteUser() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/api/users/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...prefix, 'users'] }),
  })
}

// ─── Apps (Viewers) ──────────────────────────────────────────

export function useRenameApp() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const { data } = await api.patch(`/api/viewers/${id}`, { title })
      return data
    },
    onSuccess: (_data, { id }) => {
      invalidateLegacyViewerConfig(id)
      qc.invalidateQueries({ queryKey: [...prefix, 'project'] })
    },
  })
}

export function useMoveApp() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({ id, targetProjectId }: { id: string; targetProjectId: string }) => {
      const { data } = await api.post(`/api/viewers/${id}/move`, { targetProjectId })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...prefix, 'project'] })
      qc.invalidateQueries({ queryKey: [...prefix, 'projects'] })
      qc.invalidateQueries({ queryKey: [...prefix, 'projects-with-counts'] })
    },
  })
}

export function useToggleAppPublic() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({ id, isPublic }: { id: string; isPublic: boolean }) => {
      const { data } = await api.patch(`/api/viewers/${id}`, { public: isPublic })
      return data
    },
    onSuccess: (_data, { id }) => {
      invalidateLegacyViewerConfig(id)
      qc.invalidateQueries({ queryKey: [...prefix, 'project'] })
    },
  })
}

export function useDeleteApp() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post('/api/delete-project', { id })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...prefix, 'project'] }),
  })
}

// ─── Delete Project ──────────────────────────────────────

export function useUpdateAppFields() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({ id, ...fields }: { id: string } & Record<string, any>) => {
      const { data } = await api.patch(`/api/viewers/${id}`, fields)
      return data
    },
    onSuccess: (_data, { id }) => {
      invalidateLegacyViewerConfig(id)
      qc.invalidateQueries({ queryKey: [...prefix, 'project'] })
    },
  })
}

export function useDuplicateApp() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title?: string }) => {
      const { data } = await api.post(`/api/viewers/${id}/duplicate`, { title })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...prefix, 'project'] })
      qc.invalidateQueries({ queryKey: [...prefix, 'projects-with-counts'] })
    },
  })
}

export function useDeleteProject() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/api/projects/${id}`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...prefix, 'projects'] })
      qc.invalidateQueries({ queryKey: [...prefix, 'projects-with-counts'] })
    },
  })
}